/**
 * One change signal for everything a question's saved state is built from.
 *
 * **The bug this exists to kill.** The bank, the assessment paper and Add
 * question each read a question's overlay once, kept the result in their own
 * React state, and never heard about a later write. Fix a finding in the
 * sheet and the table behind it still said *Needs fixing*; open the same
 * question from another surface and it came back unfixed until a reload.
 *
 * Every writer (the overlay store, the per-assessment session store) calls
 * `notifyQuestionRecordsChanged`, and every surface that derives rows from
 * those stores subscribes with `useQuestionRecordsVersion`. A fix made
 * anywhere re-derives everywhere, without a surface having to know which
 * other surfaces exist.
 */

import * as React from "react"

let version = 0
const listeners = new Set<() => void>()
let storageBound = false

/** Storage keys whose change in another tab should refresh this one. */
const WATCHED_KEY_MARKS = [
  "library-bank-row-overlays",
  "assessment-question-history",
]

function bindStorageOnce() {
  if (storageBound || typeof window === "undefined") return
  storageBound = true
  window.addEventListener("storage", (event) => {
    const key = event.key
    if (key && !WATCHED_KEY_MARKS.some((mark) => key.includes(mark))) return
    notifyQuestionRecordsChanged()
  })
}

export function notifyQuestionRecordsChanged() {
  version += 1
  for (const listener of [...listeners]) listener()
}

export function subscribeQuestionRecords(listener: () => void) {
  bindStorageOnce()
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function getQuestionRecordsVersion() {
  return version
}

/**
 * Re-renders the caller whenever any question's saved state changes.
 *
 * Put the returned number in the dependency list of whatever derives rows from
 * the stores.
 */
export function useQuestionRecordsVersion(): number {
  return React.useSyncExternalStore(
    subscribeQuestionRecords,
    getQuestionRecordsVersion,
    getQuestionRecordsVersion,
  )
}
